<script setup lang="ts">
import { ref } from 'vue';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue';
import { FLOATING_OVERLAY_Z_INDEX } from '../layering/layering';
import type { HoverPopoverProps } from './hover-popover';

const props = withDefaults(defineProps<HoverPopoverProps>(), {
  placement: 'bottom',
});

const triggerRef = ref<HTMLElement | null>(null);
const popoverRef = ref<HTMLElement | null>(null);
const isVisible = ref(false);

const POPOVER_OFFSET = 8;
const POPOVER_SHIFT_PADDING = 8;

const { floatingStyles } = useFloating(triggerRef, popoverRef, {
  placement: () => props.placement,
  whileElementsMounted: autoUpdate,
  strategy: 'fixed',
  middleware: [
    offset(POPOVER_OFFSET),
    flip(),
    shift({ padding: POPOVER_SHIFT_PADDING }),
  ],
});

function show(): void {
  isVisible.value = true;
}

function hide(): void {
  isVisible.value = false;
}
</script>

<template>
  <div
    ref="triggerRef"
    class="inline-flex"
    @mouseenter="show"
    @mouseleave="hide"
    @focusin="show"
    @focusout="hide"
  >
    <slot name="trigger" />
  </div>
  <Teleport v-if="isVisible" to="body">
    <div
      ref="popoverRef"
      :style="{ ...floatingStyles, zIndex: FLOATING_OVERLAY_Z_INDEX }"
      class="min-w-32 rounded-xl border border-neutral-200 bg-white p-3 shadow-lg"
      @mouseenter="show"
      @mouseleave="hide"
    >
      <slot />
    </div>
  </Teleport>
</template>
